import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Loader2, Calendar, Sparkles, Check, Zap, Shield, Package, Star, ChevronRight, Coins, X, Eye } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { buyPremium, getPremiums, type PremiumPackage } from '@/shared/services/cashService';
import { useToast } from '@/shared/contexts/ToastContext';
import { getActiveTier, getPremiumTier, type PremiumTier } from '@/shared/lib/premiumTiers';
import { useConfirm, ConfirmHighlight } from '@/shared/components/ui/ConfirmDialog';

/**
 * Beneficios fixos por ServiceKind (1=Lendário, 2=Mítico, 3=Épico).
 * O "Title" e "PriceCash" e "Days" vem do backend (cadastro do plano no painel).
 * Os beneficios sao definidos pelo tier (ServiceKind) — independente do plano.
 */
type TierBenefits = {
  recursos: string[];
  status: string[];
  itens: string[];
};

const BENEFITS_BY_SERVICEKIND: Record<number, TierBenefits> = {
  // 3 = Épico
  3: {
    recursos: [
      'Acesso a canais premium com quantia de drop maior',
      'Autoloot',
      'Autoquest',
      'Teleporte para calabouço',
      'Atalho para selecionar itens para destruir',
      'Modos de câmera e zoom',
    ],
    status: [
      'Expandir até 4ª aba Inventário',
      'Bônus EXP 100%',
      'Aumento da Taxa de Drop 30%',
      'Bônus WEXP 20%',
      'Bônus Slot Loja do Agente 7 Item',
      'Aumentar Quantidade de Registro 25 Item',
      'AXP Bônus 100%',
      'Aumento de T-Point 50%',
      'Aumento de Slot de Pedido de Criação 1 Item',
      'Taxa de Drop de Caixa Aumentada 50%',
    ],
    itens: [
      'Cartão de Loja Remota — Tempo Limitado (30 dias)',
    ],
  },
  // 2 = Mítico
  2: {
    recursos: [
      'Acesso a canais premium com quantia de drop maior',
      'Autoloot',
      'Autoquest',
      'Teleporte para calabouço',
      'Atalho para selecionar itens para destruir',
      'Modos de câmera e zoom',
      'Buffs de classes no relog',
      'Macro BM3',
    ],
    status: [
      'Expandir até 5ª aba Inventário',
      'Bônus EXP 200%',
      'Aumento da Taxa de Drop 50%',
      'Bônus WEXP 50%',
      'Expandir até 3ª aba Armazém',
      'Bônus Slot Loja do Agente 10 Item',
      'Aumentar Quantidade de Registro 45 Item',
      'AXP Bônus 300%',
      'Aumento de T-Point 70%',
      'Aumento de Slot de Pedido de Criação 2 Item',
      'Taxa de Drop de Caixa Aumentada 50%',
    ],
    itens: [
      'Cartão de Loja Remota — Tempo Limitado (30 dias)',
      'Cartão de Loja do Agente Remoto (30 dias)',
      'Cartão de Armazém Remoto (30 dias)',
      'Poção de HP Inesgotável Lv.4 (30 dias)',
    ],
  },
  // 1 = Lendário
  1: {
    recursos: [
      'Acesso a canais premium com quantia de drop maior',
      'Autoloot',
      'Autoquest',
      'Auto-coleção',
      'Teleporte para calabouço',
      'Atalho para selecionar itens para destruir',
      'Modos de câmera e zoom',
      'Buffs de classes no relog',
      'Macro BM3',
      'Auto destruir',
    ],
    status: [
      'Expandir até 6ª aba Inventário',
      'Bônus EXP 300%',
      'Aumento da Taxa de Drop 70%',
      'Bônus WEXP 100%',
      'Expandir até 4ª aba Armazém',
      'Bônus Slot Loja do Agente 13 Item',
      'Aumentar Quantidade de Registro 65 Item',
      'AXP Bônus 300%',
      'Aumento de T-Point 100%',
      'Aumento de Slot de Pedido de Criação 3 Itens',
      'Taxa de Drop de Caixa Aumentada 50%',
    ],
    itens: [
      'Sócio VIP da Yekaterina (30 dias)',
      'Cartão de Loja do Agente Remoto (30 dias)',
      'Cartão de Loja Remota — Tempo Limitado (30 dias)',
      'Cartão de Armazém Remoto (30 dias)',
      'Poção de HP Inesgotável Lv.4 (30 dias)',
      'Poção de MP Inesgotável Lv.4 (30 dias)',
      'Cartão de Pedido Remoto (30 dias)',
    ],
  },
};

const EMPTY_BENEFITS: TierBenefits = { recursos: [], status: [], itens: [] };

function getBenefits(serviceKind: number): TierBenefits {
  return BENEFITS_BY_SERVICEKIND[serviceKind] ?? EMPTY_BENEFITS;
}

/** Aplica o desconto e retorna preco final (inteiro). */
function applyDiscount(priceCash: number, discount: number): number {
  const d = Math.max(0, Math.min(100, discount || 0));
  return Math.round(priceCash * (100 - d) / 100);
}

export default function PremiumPage() {
  const { cash, premium, refreshCash, refreshPremium } = useAuth();
  const { notify } = useToast();
  const confirm = useConfirm();

  const [plans, setPlans] = useState<PremiumPackage[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [buying, setBuying] = useState<number | null>(null);
  const [detailsPlan, setDetailsPlan] = useState<PremiumPackage | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingPlans(true);
    getPremiums()
      .then((rows) => {
        if (cancelled) return;
        // Ordena por preco (menor -> maior). Backend ja faz isso, mas garantimos no front.
        setPlans([...rows].sort((a, b) => (a.priceCash || 0) - (b.priceCash || 0)));
      })
      .catch((err: any) => notify('error', err?.message || 'Erro ao carregar planos'))
      .finally(() => { if (!cancelled) setLoadingPlans(false); });
    return () => { cancelled = true; };
  }, [notify]);

  const premiumDays = premium?.expireDate
    ? Math.max(0, Math.ceil((new Date(premium.expireDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const activeTier = getActiveTier(premium?.serviceKind, premiumDays);

  const handleBuy = async (plan: PremiumPackage) => {
    const tier = getPremiumTier(plan.serviceKind);
    const finalPrice = applyDiscount(plan.priceCash, plan.discount ?? 0);

    const ok = await confirm({
      title: `Ativar ${plan.title || tier.name}?`,
      description: `Seu plano ficara ativo por ${plan.days} dias. Os itens bonus sao entregues imediatamente.`,
      accentColor: tier.color,
      icon: <Crown size={24} />,
      confirmLabel: 'Confirmar Compra',
      body: (
        <ConfirmHighlight
          icon={<Coins size={18} />}
          label="Valor a debitar"
          value={`${finalPrice.toLocaleString('pt-BR')} lcoin`}
          color={tier.color}
        />
      ),
    });
    if (!ok) return;

    // Checa saldo ANTES de chamar API — UX melhor que esperar 500
    if ((cash?.cash ?? 0) < finalPrice) {
      notify('error',
        `lcoin insuficiente. Voce tem ${(cash?.cash ?? 0).toLocaleString('pt-BR')}, ` +
        `precisa de ${finalPrice.toLocaleString('pt-BR')}.`);
      return;
    }

    setBuying(plan.id);
    try {
      const res = await buyPremium(plan.id);
      notify('success', res.message || `${plan.title || tier.name} ativado!`);
      // Refresh em paralelo — atualiza saldo cash + plano ativo no header
      await Promise.all([refreshCash(), refreshPremium()]);
    } catch (err: any) {
      // Backend manda mensagem util em err.message (ex: "Você não tem X Cash para...")
      const msg = err?.message || err?.detail?.Message || err?.detail?.message || 'Erro ao comprar premium';
      notify('error', typeof msg === 'string' ? msg : 'Erro ao comprar premium');
    } finally {
      setBuying(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Premium</h1>
      </div>

      {/* Status atual — destaca plano + dias restantes + data expiracao */}
      <div
        className="surface-card rounded-md p-6 mb-8 flex items-center gap-5"
        style={activeTier.isPaid ? { borderColor: activeTier.borderSoft, boxShadow: `0 0 35px ${activeTier.glow}` } : undefined}
      >
        <div
          className="w-16 h-16 rounded-md flex items-center justify-center shrink-0"
          style={{ background: activeTier.bgSoft }}
        >
          <Crown size={32} style={{ color: activeTier.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-white/65 uppercase tracking-wider font-bold">Premium Atual</p>
          <p className="text-2xl md:text-3xl font-extrabold leading-tight" style={{ color: activeTier.color }}>
            {activeTier.name}
          </p>
          {!activeTier.isPaid && (
            <p className="text-xs text-white/70 mt-1">{activeTier.description}</p>
          )}
        </div>
        {activeTier.isPaid && (
          <div className="ml-auto text-right shrink-0">
            <p className="text-3xl md:text-4xl font-black leading-none" style={{ color: activeTier.color }}>
              {premiumDays}
              <span className="text-sm font-bold text-white/65 ml-1">dias</span>
            </p>
            <p className="text-[11px] text-white/55 mt-1.5 flex items-center justify-end gap-1">
              <Calendar size={11} />
              Expira em {new Date(premium!.expireDate).toLocaleDateString('pt-BR')}
            </p>
          </div>
        )}
      </div>

      {/* Planos — cards compactos */}
      {loadingPlans ? (
        <div className="surface-card rounded-md py-16 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : plans.length === 0 ? (
        <div className="surface-card rounded-md py-16 text-center text-white/40">
          Nenhum plano disponível no momento.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const tier = getPremiumTier(plan.serviceKind);
            const isTop = plan.serviceKind === 1;
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                tier={tier}
                isTop={isTop}
                isBuying={buying === plan.id}
                onBuy={() => handleBuy(plan)}
                onShowDetails={() => setDetailsPlan(plan)}
              />
            );
          })}
        </div>
      )}

      {/* Modal de detalhes */}
      <PlanDetailsModal
        plan={detailsPlan}
        tier={detailsPlan ? getPremiumTier(detailsPlan.serviceKind) : null}
        isBuying={detailsPlan ? buying === detailsPlan.id : false}
        onClose={() => setDetailsPlan(null)}
        onBuy={() => { if (detailsPlan) handleBuy(detailsPlan); }}
      />

      {/* Aviso: nao acumula (mesmo estilo discreto do card abaixo) */}
      <div className="mt-6 surface-card rounded-md p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center shrink-0">
          <Sparkles size={14} className="text-white/30" />
        </div>
        <p className="text-xs text-white/70 leading-relaxed">
          O Premium <strong className="text-white/85">não acumula</strong>.
          Uma nova compra substitui o plano atual (plano e dias restantes).
        </p>
      </div>

      {/* Info extra (duracao + vinculo a conta) */}
      <div className="mt-3 surface-card rounded-md p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center shrink-0">
          <Sparkles size={14} className="text-white/30" />
        </div>
        <p className="text-xs text-white/70 leading-relaxed">
          Todos os itens são vinculados à conta e têm duração de 30 dias a partir da ativação.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  Plan card — COMPACTO (so preview, detalhes no modal)
// ────────────────────────────────────────────────────────────────
function PlanCard({
  plan, tier, isTop, isBuying, onBuy, onShowDetails,
}: {
  plan: PremiumPackage;
  tier: PremiumTier;
  isTop: boolean;
  isBuying: boolean;
  onBuy: () => void;
  onShowDetails: () => void;
}) {
  const benefits = getBenefits(plan.serviceKind);
  const totalBeneficios = benefits.recursos.length + benefits.status.length + benefits.itens.length;
  const c = tier.color;
  const discount = plan.discount ?? 0;
  const hasDiscount = discount > 0;
  const finalPrice = applyDiscount(plan.priceCash, discount);

  return (
    <div
      className="premium-card group relative flex flex-col cursor-default rounded-md px-6 pt-7 pb-6 transition-all duration-300"
      style={{
        background: 'linear-gradient(180deg, #0b0f12 0%, #060809 100%)',
        border: `1px solid ${c}${isTop ? '55' : '33'}`,
        boxShadow: isTop
          ? `0 8px 24px -14px rgba(0,0,0,0.9), 0 0 32px ${tier.glow}, inset 0 1px 0 rgba(255,255,255,0.03)`
          : `0 8px 24px -14px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.03)`,
        ['--c' as any]: c,
        ['--c-glow' as any]: tier.glow,
      }}
    >
      {/* Badge no canto superior direito (só no top tier) */}
      {isTop && (
        <div className="absolute -top-3 right-4 z-10">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest"
            style={{
              background: `linear-gradient(135deg, ${c}, ${c}cc)`,
              color: '#0a0e14',
              boxShadow: `0 0 18px ${tier.glow}`,
            }}
          >
            <Sparkles size={10} /> Melhor Valor
          </span>
        </div>
      )}

      {/* Header — ícone grande no topo */}
      <div className="flex flex-col items-center text-center mb-5">
        <div
          className="inline-flex items-center justify-center w-16 h-16 rounded-md mb-3 transition-all duration-300"
          style={{
            background: `radial-gradient(circle at 50% 30%, ${c}33, ${c}0a 70%)`,
            border: `1px solid ${c}55`,
            boxShadow: `0 0 22px ${tier.glow}, inset 0 0 16px ${c}14`,
          }}
        >
          <Crown size={30} style={{ color: c, filter: `drop-shadow(0 0 6px ${c})` }} />
        </div>
        <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] mb-1" style={{ color: c }}>Plano</p>
        <h3
          className="text-2xl font-black tracking-tight text-white leading-tight"
          style={{ textShadow: `0 0 10px ${c}66` }}
        >
          {plan.title || tier.name}
        </h3>
      </div>

      {/* Preço — riscado se tem desconto */}
      <div className="text-center min-h-[68px] flex flex-col justify-end">
        {hasDiscount && (
          <p className="text-base text-white/45 line-through font-bold leading-tight">
            {plan.priceCash.toLocaleString('pt-BR')} lcoin
          </p>
        )}
        <div className="flex items-baseline justify-center gap-1.5">
          <span className="text-4xl font-black text-white leading-none">
            {finalPrice.toLocaleString('pt-BR')}
          </span>
          <span className="text-xs text-white/65 font-bold uppercase tracking-wider lcoin">lcoin</span>
        </div>
        <p className="text-[11px] text-white/55 mt-1">por {plan.days} dias</p>
      </div>

      {/* Separador */}
      <div className="h-px w-12 mx-auto my-5" style={{ background: `linear-gradient(to right, transparent, ${c}55, transparent)` }} />

      {/* Quick stats — neutros, sem cores berrantes */}
      <div className="grid grid-cols-3 gap-2 text-center mb-5">
        <QuickStat icon={<Zap size={12} />} value={benefits.recursos.length} label="Recursos" />
        <QuickStat icon={<Shield size={12} />} value={benefits.status.length} label="Status" />
        <QuickStat icon={<Package size={12} />} value={benefits.itens.length} label="Itens" />
      </div>

      {/* CTA + detalhes */}
      <div className="mt-auto flex flex-col gap-3">
        <button
          disabled={isBuying}
          onClick={onBuy}
          className="premium-btn w-full py-3 rounded-md text-sm font-extrabold uppercase tracking-widest transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          style={{
            background: `${c}1a`,
            color: `#${c.slice(1)}`,
            border: `1px solid ${c}55`,
          }}
        >
          {isBuying ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              Ativar
              <ChevronRight size={16} />
            </>
          )}
        </button>

        {/* Botão "Ver N benefícios" — mais destacado (era texto fraco antes) */}
        <button
          onClick={onShowDetails}
          className="w-full py-2.5 rounded-md text-xs font-bold flex items-center justify-center gap-2 cursor-pointer transition-all"
          style={{
            background: `${c}10`,
            color: '#ffffff',
            border: `1px solid ${c}40`,
          }}
        >
          <Eye size={14} style={{ color: c }} />
          Ver {totalBeneficios} benefícios
        </button>
      </div>

      {/* Hover styles */}
      <style>{`
        .premium-card { will-change: transform; }
        .premium-card:hover {
          transform: translateY(-6px);
          border-color: var(--c) !important;
          box-shadow:
            0 18px 40px rgba(0,0,0,0.65),
            0 0 38px var(--c-glow),
            inset 0 1px 0 rgba(255,255,255,0.08) !important;
        }
        .premium-card:hover .premium-btn {
          background: var(--c) !important;
          color: #0a0e14 !important;
          border-color: var(--c) !important;
          box-shadow: 0 0 18px var(--c-glow);
        }
      `}</style>
    </div>
  );
}

function QuickStat({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div className="rounded-md py-2 px-1 bg-white/[0.03] border border-white/[0.06]">
      <div className="flex items-center justify-center gap-1 mb-0.5 text-white/85">
        {icon}
        <span className="text-sm font-extrabold leading-none tabular-nums">{value}</span>
      </div>
      <p className="text-[9px] text-white/55 uppercase tracking-wider font-bold">{label}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
//  Plan details modal — abre centralizado com todas as secoes
// ────────────────────────────────────────────────────────────────
function PlanDetailsModal({
  plan, tier, isBuying, onClose, onBuy,
}: {
  plan: PremiumPackage | null;
  tier: PremiumTier | null;
  isBuying: boolean;
  onClose: () => void;
  onBuy: () => void;
}) {
  const open = !!plan && !!tier;
  const benefits = plan ? getBenefits(plan.serviceKind) : EMPTY_BENEFITS;
  const discount = plan?.discount ?? 0;
  const hasDiscount = discount > 0;
  const finalPrice = plan ? applyDiscount(plan.priceCash, discount) : 0;

  return createPortal(
    <AnimatePresence>
      {open && plan && tier && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal — landscape: 3 secoes lado a lado em md+ */}
          <motion.div
            className="relative w-full max-w-3xl max-h-[78vh] rounded-md p-[1.5px] overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            style={{
              background: `linear-gradient(135deg, ${tier.color}, ${tier.color}50 60%, ${tier.color}15)`,
              boxShadow: `0 0 60px ${tier.glow}`,
            }}
          >
            <div className="rounded-md bg-[#070b0e]/97 backdrop-blur-xl flex flex-col max-h-[calc(78vh-3px)]">
              {/* Close */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition cursor-pointer"
                aria-label="Fechar"
              >
                <X size={14} />
              </button>

              {/* Header — landscape: icon esquerda + nome/preco direita */}
              <div
                className="px-5 py-4 border-b flex items-center gap-4"
                style={{ borderColor: tier.borderSoft }}
              >
                <div
                  className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-md"
                  style={{
                    background: `linear-gradient(135deg, ${tier.color}30, ${tier.color}10)`,
                    boxShadow: `inset 0 0 18px ${tier.color}20`,
                  }}
                >
                  <Crown size={22} style={{ color: tier.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-extrabold tracking-tight truncate" style={{ color: tier.color }}>
                    {plan.title || tier.name}
                  </h3>
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    {hasDiscount && (
                      <span className="text-sm text-white/45 line-through font-bold">
                        {plan.priceCash.toLocaleString('pt-BR')}
                      </span>
                    )}
                    <span className="text-2xl font-black" style={{ color: tier.color }}>
                      {finalPrice.toLocaleString('pt-BR')}
                    </span>
                    <span className="text-[11px] text-white/65 font-semibold">lcoin · {plan.days} dias</span>
                  </div>
                </div>
              </div>

              {/* Scrollable content — 3 colunas lado a lado em md+ */}
              <div className="overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <PlanSection
                  icon={<Zap size={14} />}
                  title="Recursos"
                  items={benefits.recursos}
                  tier={tier}
                  iconBg="rgba(168, 85, 247, 0.15)"
                  iconColor="#a855f7"
                />
                <PlanSection
                  icon={<Shield size={14} />}
                  title="Status"
                  items={benefits.status}
                  tier={tier}
                  iconBg="rgba(59, 130, 246, 0.15)"
                  iconColor="#3b82f6"
                />
                <PlanSection
                  icon={<Package size={14} />}
                  title="Itens"
                  items={benefits.itens}
                  tier={tier}
                  iconBg={tier.bgSoft}
                  iconColor={tier.color}
                  useStar
                />
              </div>

              {/* Footer fixo */}
              <div
                className="px-4 py-3 border-t flex items-center gap-3"
                style={{ borderColor: tier.borderSoft, background: `${tier.color}06` }}
              >
                <button
                  onClick={onClose}
                  className="flex-1 py-2.5 rounded-md text-sm font-bold text-white/75 bg-white/5 hover:bg-white/10 border border-white/10 transition cursor-pointer"
                >
                  Fechar
                </button>
                <button
                  disabled={isBuying}
                  onClick={onBuy}
                  className="flex-[2] py-2.5 rounded-md text-sm font-extrabold flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 transition"
                  style={{
                    background: `linear-gradient(135deg, ${tier.color}, ${tier.color}dd)`,
                    color: tier.serviceKind === 1 ? '#000' : '#fff',
                    boxShadow: `0 0 25px ${tier.glow}`,
                  }}
                >
                  {isBuying ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      Ativar {plan.title || tier.name}
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function PlanSection({
  icon, title, items, tier, iconBg, iconColor, useStar = false,
}: {
  icon: React.ReactNode;
  title: string;
  items: string[];
  tier: PremiumTier;
  iconBg: string;
  iconColor: string;
  useStar?: boolean;
}) {
  if (items.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: iconBg, color: iconColor }}>
            {icon}
          </div>
          <h4 className="text-xs font-bold text-white/65 uppercase tracking-wider">{title}</h4>
        </div>
        <p className="text-[12px] text-white/40 italic">Nenhum</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: iconBg, color: iconColor }}
        >
          {icon}
        </div>
        <h4 className="text-xs font-bold text-white/65 uppercase tracking-wider">{title}</h4>
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-[13px]">
            {useStar ? (
              <Star size={13} className="mt-0.5 shrink-0" style={{ color: tier.color }} />
            ) : (
              <Check size={14} className="mt-0.5 shrink-0" style={{ color: tier.color }} />
            )}
            <span className="text-white/70 leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
